import { ApolloProvider } from '@apollo/react-hooks';
import { ThemeProvider as BowkitThemeProvider } from '@emotion/react';
import { MuiThemeProvider } from '@material-ui/core/styles';
import { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { BrowserRouter as Router } from 'react-router-dom';
import { produce } from 'immer';
import { IframeBaselineStyles } from './BaseLineStyles';
import QuotationWidget from './QuotationWidget';
import Header from './QuotationWidget/Header';
import client from './client';
import { BASENAME } from './constants';
import {
  AttributeAndQsContextProps,
  AttributeAndQsContextProvider,
} from './contexts/AttributeAndQsContext';
import { LocaleProvider } from './contexts/LocaleContext';
import { QuotationProvider } from './contexts/QuotationContext';
import { UserAuthProvider } from './contexts/UserAuthContext';
import theme from './helpers/theme';
import {
  AuthProvider,
  createStatsigClient,
  CustomIds,
  ENVIRONMENT,
  ExperimentProvider,
  ExperimentProviderProps,
  retrieveTrackingAlias,
  USER_CHANGE_CALLBACK,
  UserChangeCallback,
} from '@bowtie-web/common';
import { BowtieDialogSpinner } from '@bowtie-web/components';
import Notifier from './Notifier';

type AppProps = {
  attributeValues?: AttributeAndQsContextProps;
};

const onUserChange: UserChangeCallback = (user, prevUser) => {
  USER_CHANGE_CALLBACK.LOGGER(user, prevUser);
  USER_CHANGE_CALLBACK.TRACKING(user, prevUser);
};

const statsigClientSdkKey =
  import.meta.env.REACT_APP_STATSIG_CLIENT_SDK_KEY || '';

const statsigClient = createStatsigClient({
  clientSdkKey: statsigClientSdkKey,
  environment: ENVIRONMENT,
});

const initializeExperimentUser: ExperimentProviderProps['initializeUser'] =
  async user => {
    const { id } = await retrieveTrackingAlias();
    const updated = produce(user, draft => {
      draft.customIDs = { [CustomIds.AliasId]: id };
    });
    return updated;
  };

export const ThemeProvider: FC = ({ children }) => {
  const { locale: lang } = useIntl();

  const locale = useMemo(() => (lang.includes('zh') ? 'zh' : 'en'), [lang]);

  return (
    <MuiThemeProvider theme={theme}>
      <BowkitThemeProvider theme={{ ...theme, locale }}>
        <IframeBaselineStyles />
        {children}
      </BowkitThemeProvider>
    </MuiThemeProvider>
  );
};

const App = ({ attributeValues }: AppProps) => (
  <Router basename={BASENAME}>
    <AttributeAndQsContextProvider attributeValues={attributeValues}>
      <LocaleProvider>
        <ThemeProvider>
          <ApolloProvider client={client}>
            <ExperimentProvider
              fallback={<BowtieDialogSpinner open />}
              initializeUser={initializeExperimentUser}
              client={statsigClient}
            >
              <AuthProvider onUserChange={onUserChange}>
                <UserAuthProvider>
                  <QuotationProvider>
                    <Header />
                    <Notifier />
                    <QuotationWidget />
                  </QuotationProvider>
                </UserAuthProvider>
              </AuthProvider>
            </ExperimentProvider>
          </ApolloProvider>
        </ThemeProvider>
      </LocaleProvider>
    </AttributeAndQsContextProvider>
  </Router>
);

export default App;
